import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Avatar,
  Box,
  FormControl,
  FormHelperText,
  Grid,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { MultiSelect } from "react-multi-select-component";
import styled from "@emotion/styled";
import { LoadingButton } from "@mui/lab";

function LiveMonitor() {
  const { token } = useSelector((state) => state.token.value);
  const [loading, setLoading] = useState(false);
  const [supervisors, setSupervisors] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState([]);
  const [selectedSupervisor, setSelectedSupervisor] = useState([]);

  const [supervisorDetails, setSupervisorDetails] = useState([]);

  useEffect(() => {
    const districtSet = new Set();
    // Extract unique districts
    supervisors.forEach((item) => {
      if (item.district) {
        districtSet.add(item.district.trim());
      }
    });

    // Convert to array of objects
    const allDistricts = Array.from(districtSet).map((district) => ({
      label: district,
      value: district,
    }));
    setSelectedDistrict(allDistricts);

    const allSupervisors = supervisors.map((supervisor) => ({
      label: `${supervisor.firstName} ${supervisor.lastName}/${supervisor.district}`,
      value: supervisor._id,
    }));
    setSelectedSupervisor(allSupervisors);
    allSupervisors.length > 0 && getSupervisorDetails(allSupervisors);
  }, [supervisors]);

  useEffect(() => {
    if (selectedSupervisor.length === 0) return;
    const id = setInterval(() => {
      !loading && getSupervisorDetails(selectedSupervisor);
    }, 10000);
    return () => clearInterval(id);
  }, [selectedSupervisor, loading]);

  // api call to get supervisor list
  const getSupervisorList = async () => {
    try {
      setLoading(true);
      const response = await axios({
        method: "post",
        url: `${process.env.REACT_APP_ADMINNODE}/api/v1/admin/getLiveTrackUser`,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: {
          role: "supervisor",
        },
      });
      if (response.data.status !== 100) return;
      setSupervisors(response.data.result);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const sortByIdleTimeDescending = (data) => {
    const toSeconds = (timeStr) => {
      const [hours, minutes, seconds] = timeStr.split(":").map(Number);
      return hours * 3600 + minutes * 60 + seconds;
    };

    return data.sort((a, b) => {
      const aIdle = toSeconds(a.idleTime);
      const bIdle = toSeconds(b.idleTime);
      return bIdle - aIdle; // descending
    });
  };

  const isIdleTimeMoreThan30Min = (user) => {
    const [hours, minutes, seconds] = user.idleTime.split(":").map(Number);
    const totalSeconds = hours * 3600 + minutes * 60 + seconds;

    return totalSeconds > 1800; // 30 minutes = 1800 seconds
  };

  const getSupervisorDetails = async (allSupervisors) => {
    const supervisorIds = allSupervisors
      ? allSupervisors?.map((supervisor) => supervisor.value)
      : selectedSupervisor?.map((supervisor) => supervisor.value);
    try {
      setLoading(true);
      const response = await axios({
        method: "post",
        url: `${process.env.REACT_APP_NODESERVER}/api/v1/file/manage/liveWorkMonitor`,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: {
          userIds: supervisorIds,
        },
      });
      if (response.data.status !== 100) return;
      setSupervisorDetails(sortByIdleTimeDescending(response.data.result));
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSupervisorList();
  }, []);

  const districtSet = new Set();
  // Extract unique districts
  supervisors.forEach((item) => {
    if (item.district) {
      districtSet.add(item.district.trim());
    }
  });

  // Convert to array of objects
  const districtListDropdown = Array.from(districtSet).map((district) => ({
    label: district,
    value: district,
  }));

  // Filtered supervisors based on selectedDistrict
  const filteredSupervisors = supervisors.filter((supervisor) =>
    selectedDistrict.length === 0
      ? true // if nothing selected, show all
      : selectedDistrict.some(
          (district) => district.value === supervisor.district
        )
  );

  const supervisorListDropdown = filteredSupervisors.map((supervisor) => ({
    label: `${supervisor.firstName} ${supervisor.lastName}/${supervisor.district}`,
    value: supervisor._id,
  }));

  useEffect(() => {
    const filteredSupervisors = supervisors.filter((supervisor) =>
      selectedDistrict.length === 0
        ? true // if nothing selected, show all
        : selectedDistrict.some(
            (district) => district.value === supervisor.district
          )
    );

    const supervisorListDropdown = filteredSupervisors.map((supervisor) => ({
      label: `${supervisor.firstName} ${supervisor.lastName}/${supervisor.district}`,
      value: supervisor._id,
    }));
    setSelectedSupervisor(supervisorListDropdown);
  }, [selectedDistrict]);

  return (
    <Box p={1} pt={4}>
      <Typography fontWeight={600} fontSize={16} variant="h6">
        Live Monitor
      </Typography>
      <FormControl sx={{ m: 1, width: 200, height: 100 }}>
        <FormHelperText sx={{ fontSize: "1em", color: "#000" }}>
          District
        </FormHelperText>
        <div style={{ height: "200px", color: "#000" }}>
          <MultiSelect
            options={districtListDropdown}
            value={selectedDistrict}
            onChange={setSelectedDistrict}
            labelledBy="State"
          />
        </div>
      </FormControl>
      <FormControl sx={{ mt: 1, width: 500, height: 100 }}>
        <FormHelperText sx={{ fontSize: "1em", color: "#000" }}>
          Supervisors
        </FormHelperText>
        <div
          style={{
            color: "#000",
            display: "flex",
            gap: 10,
            alignItems: "center",
          }}
        >
          <Box width={300}>
            <MultiSelect
              options={supervisorListDropdown}
              value={selectedSupervisor}
              onChange={setSelectedSupervisor}
              labelledBy="supervisor"
            />
          </Box>
          <ApplyFilter
            loading={loading}
            onClick={() => getSupervisorDetails()}
          />
        </div>
      </FormControl>
      {supervisorDetails.length === 0 ? (
        <Grid mt={5} container justifyContent="center">
          No data to show
        </Grid>
      ) : (
        <Box display={"flex"} flexWrap={"wrap"} gap={1}>
          {supervisorDetails.map((supervisor) => (
            <Box>
              <StyledBox
                style={{
                  borderColor: isIdleTimeMoreThan30Min(supervisor)
                    ? "#f70f0f"
                    : "",
                  backgroundColor: isIdleTimeMoreThan30Min(supervisor)
                    ? "rgba(230, 2, 2, 0.2)"
                    : "",
                }}
              >
                <TruncatedName
                  firstName={supervisor.firstName}
                  lastName={supervisor.lastName}
                />
                <Avatar
                  sx={{
                    my: 1,
                    alignSelf: "center",
                    width: "130px",
                    height: "130px",
                    borderRadius: "50%",
                  }}
                  src={supervisor?.url}
                  alt={supervisor?.firstName?.[0]}
                />
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography textAlign={"left"} fontSize={11} fontWeight={500}>
                    First activity
                  </Typography>
                  <Typography
                    textAlign={"right"}
                    fontSize={10}
                    fontWeight={600}
                  >
                    {supervisor.firstAction}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="start"
                >
                  <Typography textAlign={"left"} fontSize={11} fontWeight={500}>
                    First activity time
                  </Typography>
                  <Typography
                    textAlign={"right"}
                    fontSize={10}
                    fontWeight={600}
                    whiteSpace="break-spaces"
                  >
                    {new Date(supervisor.firstDate).toLocaleTimeString()}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography textAlign={"left"} fontSize={11} fontWeight={500}>
                    Last activity
                  </Typography>
                  <Typography
                    textAlign={"right"}
                    fontSize={10}
                    fontWeight={600}
                  >
                    {supervisor.lastAction}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="start"
                >
                  <Typography textAlign={"left"} fontSize={11} fontWeight={500}>
                    Last activity time
                  </Typography>
                  <Typography
                    textAlign={"right"}
                    fontSize={10}
                    fontWeight={600}
                    whiteSpace="break-spaces"
                  >
                    {new Date(supervisor.lastDate).toLocaleTimeString()}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography textAlign={"left"} fontSize={11} fontWeight={500}>
                    Idle time
                  </Typography>
                  <Typography
                    textAlign={"right"}
                    fontSize={10}
                    fontWeight={600}
                  >
                    {supervisor.idleTime}
                  </Typography>
                </Stack>
              </StyledBox>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default LiveMonitor;
const StyledBox = styled(Box)({
  padding: "8px",
  width: "170px",
  borderRadius: "5px",
  display: "flex",
  flexDirection: "column",
  gap: 3,
  alignItems: "stretch",
  backgroundColor: "white",
  border: "1px solid #bdc3c9",
});

const ColorButton = styled(LoadingButton)(({ theme }) => ({
  border: "1px solid #1553b5",
  backgroundColor: "#1553b5",
  color: "#fff !important",
  padding: "4px 8px",
  margin: "auto 2px",
  "&:hover": {
    border: "1px solid #f4f4f4",
    backgroundColor: "#1553b5",
    color: "#fff !important",
  },
}));

const ApplyFilter = ({ onClick, loading }) => {
  return (
    <div>
      <ColorButton
        sx={{
          color: "#fff",
          borderRadius: "10px",
        }}
        onClick={onClick}
        loading={loading}
        loadingPosition="start"
        variant="contained"
      >
        Apply Filter
      </ColorButton>
    </div>
  );
};

const TruncatedName = ({ firstName, lastName }) => {
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const fullName = `${firstName} ${lastName}`;

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [fullName]);

  const textComponent = (
    <Typography
      ref={textRef}
      textAlign="left"
      fontSize={12}
      fontWeight={600}
      noWrap
      sx={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: "100%", // Or fixed width like 150px
        display: "block",
      }}
    >
      {fullName}
    </Typography>
  );

  return isTruncated ? (
    <Tooltip title={fullName} placement="top" arrow>
      {textComponent}
    </Tooltip>
  ) : (
    textComponent
  );
};
